import React, { ElementType, Suspense, useCallback, useState } from 'react';

import { Box, Icon, RotateIcon } from '@rbilabs/components-library';
import delve from 'dlv';
import { useIntl } from 'react-intl';

import ActionButton from 'components/action-button';
import CreditCardFormInputs from 'components/credit-card-form-inputs';
import IconCheckCircle from 'components/icons/check-circle';
import Modal, { ModalContent, ModalHeading } from 'components/modal';
import PaymentMethodSelect, { HoverTheme } from 'components/payment-method-select';
import UserVerificationModal from 'components/user-verification-modal';
import { useAddPrepaidCardMutation } from 'generated/rbi-graphql';
import useEffectOnce from 'hooks/use-effect-once';
import useErrorModal from 'hooks/use-error-modal';
import useUserHasToVerify from 'hooks/use-user-has-to-verify';
import useApplePay from 'state/apple-pay/hooks/use-apple-pay';
import { useAuthContext } from 'state/auth';
import {
  TransactionType,
  getGooglePaymentsConfigs,
} from 'state/google-pay/get-google-payments-configs';
import useGooglePay from 'state/google-pay/hooks/use-google-pay';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  defaultPaymentFieldVariation,
} from 'state/launchdarkly/variations';
import { useLogger } from 'state/logger';
import { usePaymentContext } from 'state/payment';
import { CardTypes, IApplePayDetails, IGooglePayDetails } from 'state/payment/types';
import { useUIContext } from 'state/ui';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import {
  initialErrorState,
  initialPaymentState,
  isAllowedReloadMethod,
  onCCFormChange,
  testCardPaymentState,
} from 'utils/payment';
import { isApplePay, isGooglePay } from 'utils/payment/native-payment';
import { routes } from 'utils/routing';

import {
  CenteredContent,
  CollapsibleContent,
  DisplayToggle,
  ErrorAnchor,
  FormSection,
  InstructionText,
  LeftAlignedContent,
  NormalText,
  PlacementHolder,
  SectionDescription,
  SectionHeading,
  SpacedContent,
  SubInstructionText,
  SubheadingButton,
  SubheadingContainer,
  SuccessHeading,
  TimCardText,
} from './styled';
import { IAddPrepaidCardArgs, ILoadPrepaidCardModal } from './types';
import { prepaidCardMapInput } from './utils';

enum ModalStates {
  FORM,
  SUCCESS,
}

const LoadPrepaidCardModal = ({
  onDismiss,
  loadAmount,
  modalAppearanceEventMessage = 'Order Cancelled',
  forceSuccess = false,
}: ILoadPrepaidCardModal) => {
  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;
  const enableUnverifiedSavedPayments = useFlag(LaunchDarklyFlag.ENABLE_UNVERIFIED_SAVED_PAYMENTS);
  const userHasToVerify = useUserHasToVerify(enableUnverifiedSavedPayments);
  const {
    addPaymentMethod,
    getPrepaidPaymentMethod,
    getPaymentMethods,
    paymentMethods,
    defaultReloadPaymentMethodId: reloadPaymentMethodId,
    setDefaultReloadPaymentMethodId: setReloadPaymentMethodId,
    setDefaultPaymentMethodId,
    canUseApplePay,
    canUseGooglePay,
    transformPaymentValues,
  } = usePaymentContext();
  const [addPrepaidCardMutation] = useAddPrepaidCardMutation();
  const auth = useAuthContext();
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Prepaid Account Creation Failure',
  });

  const billingCountry = delve(auth, 'user.details.isoCountryCode') || ISOs.CAN;
  const { countryCode, currencyCode } = getCountryAndCurrencyCodes(billingCountry);

  const logger = useLogger();
  const { formatMessage } = useIntl();
  const { formatCurrencyForUser } = useUIContext();

  const autoFill = useFlag(LaunchDarklyFlag.AUTO_FILL_TEST_CARD);

  const [loading, setLoading] = useState(false);
  const [prepaidCardBalance, setPrepaidCardBalance] = useState(0);
  const [errors, setErrors] = useState(initialErrorState);
  const userDetailsName = auth.user?.details.name;
  const [paymentValues, setPaymentValues] = useState(
    initialPaymentState({ billingCountry: ISOs.CAN, userDetailsName })
  );
  const [section, setSection] = useState<ModalStates>(
    forceSuccess ? ModalStates.SUCCESS : ModalStates.FORM
  );

  const handleApplePay = ({ error, data }: { error?: string; data?: IApplePayDetails }): void => {
    if (data) {
      addNewPrepaidAccount(data);
      return;
    }
    if (error) {
      logger.warn(error);
    }
    setLoading(false);
  };

  const { requestApplePayPayment } = useApplePay({
    handleApplePay,
    prepaid: true,
  });

  const { requestGooglePayPayment } = useGooglePay({
    onError: () => {
      setLoading(false);
    },
    onCompleted: (data: IGooglePayDetails) => {
      if (!data) {
        setLoading(false);
        return;
      }
      addNewPrepaidAccount(data);
    },
  });

  const displayAddNewPaymentMethodForm =
    !canUseApplePay && paymentMethods.length === 0 && !userHasToVerify;
  const [showAddNewPaymentMethodForm, setShowAddNewPaymentMethodForm] = useState(
    displayAddNewPaymentMethodForm
  );
  const [showExistingPaymentMethodForm, setShowExistingPaymentMethodForm] = useState(
    !displayAddNewPaymentMethodForm
  );

  const allowedReloadPaymentMethods = paymentMethods.filter(
    paymentMethod =>
      isAllowedReloadMethod(paymentMethod) &&
      (!reloadPaymentMethodId || reloadPaymentMethodId !== paymentMethod.fdAccountId)
  );
  const showExistingCardSection = allowedReloadPaymentMethods.length > 0 || !!reloadPaymentMethodId;
  const reloadPaymentMethod = paymentMethods.find(
    method => method.fdAccountId === reloadPaymentMethodId
  );

  const isModalStateSuccess = section === ModalStates.SUCCESS;

  useEffectOnce(() => {
    if (autoFill) {
      setPaymentValues(testCardPaymentState({ paymentFieldVariations }));
    }
    if (isModalStateSuccess && loadAmount) {
      setPrepaidCardBalance(loadAmount);
    }
  });

  const toggleAddPaymentMethodForm = () => {
    // collapse other open toggle
    setShowExistingPaymentMethodForm(false);

    setShowAddNewPaymentMethodForm(!showAddNewPaymentMethodForm);
  };

  const toggleExistingPaymentMethodForm = () => {
    // toggle between two accordions
    setShowAddNewPaymentMethodForm(false);

    setShowExistingPaymentMethodForm(!showExistingPaymentMethodForm);
  };

  const applePaySelected = isApplePay(reloadPaymentMethodId);
  const googlePaySelected = isGooglePay(reloadPaymentMethodId);

  const handlePaymentMethodOptionClick = (fdAccountId: string) => {
    // If apple pay is available in the client, append it to payment methods
    const paymentMethodToLoadWith = paymentMethods.find(paymentMethod => {
      const accountIdentifier = paymentMethod.fdAccountId ?? paymentMethod.accountIdentifier ?? '';
      return accountIdentifier === fdAccountId;
    });
    if (paymentMethodToLoadWith) {
      setReloadPaymentMethodId(
        paymentMethodToLoadWith.fdAccountId ?? paymentMethodToLoadWith.accountIdentifier ?? ''
      );
    }
  };

  const handleChange = useCallback(
    (name, value) => {
      const { state, formErrors } = onCCFormChange(
        name,
        value,
        paymentValues,
        errors,
        formatMessage,
        // @TODO: pull supportedCardTypes from Sanity
        ['AMEX', 'DINERS_CLUB', 'DISCOVER', 'JCB', 'MASTERCARD', 'VISA']
      );
      setErrors(prevState => ({ ...prevState, ...formErrors }));
      setPaymentValues(prevState => ({ ...prevState, ...state }));
    },
    [paymentValues, errors, formatMessage]
  );

  const maybeAddPaymentMethod = useCallback(() => {
    if (showAddNewPaymentMethodForm) {
      return addPaymentMethod(transformPaymentValues({ paymentValues, paymentFieldVariations }), {
        // Skip the default error dialog since we are showing a better one here
        skipErrorDialogOnError: true,
      });
    }
    return Promise.resolve(reloadPaymentMethodId);
  }, [
    addPaymentMethod,
    paymentValues,
    reloadPaymentMethodId,
    showAddNewPaymentMethodForm,
    transformPaymentValues,
    paymentFieldVariations,
  ]);

  const errorText = formatMessage(
    { id: 'addGiftCardError' },
    {
      link: <ErrorAnchor href={routes.support}>{formatMessage({ id: 'chatWithUs' })}</ErrorAnchor>,
    }
  );

  const addNewPrepaidAccount = useCallback(
    async (input?: IApplePayDetails | IGooglePayDetails): Promise<void> => {
      try {
        const fdAccountId = await maybeAddPaymentMethod();
        const args: IAddPrepaidCardArgs = { fundValue: loadAmount };

        switch (reloadPaymentMethodId) {
          case CardTypes.APPLE_PAY:
            Object.assign(args, { applePayDetails: input });
            break;
          case CardTypes.GOOGLE_PAY:
            Object.assign(args, { googlePayDetails: input });
            break;
          default: {
            // users are somehow getting to this point without one of the required inputs
            // throwing here to stop them from continuing on to the addPrepaidCardMutation that will inveitably fail
            if (!fdAccountId) {
              logger.error(
                `Cannot add prepaid card with the following fdAccountId - ${fdAccountId}`
              );
              throw new Error();
            }
            Object.assign(args, { fdAccountId });
          }
        }

        if (getPrepaidPaymentMethod()) {
          openErrorDialog({
            message: formatMessage({ id: 'prepaidAccountAlreadyExistsError' }),
          });
          return;
        }

        const response = await addPrepaidCardMutation({
          variables: { input: prepaidCardMapInput(args) },
        });

        if (response && response.data) {
          const { currentBalance } = response.data.addGiftAccount?.prepaid;
          setPrepaidCardBalance(currentBalance || 0);
          setSection(ModalStates.SUCCESS);
          // Set the checkout payment method to the prepaid card fdAccountId coming from the response
          setDefaultPaymentMethodId(response.data.addGiftAccount?.fdAccountId || '');
        }
        getPaymentMethods();
      } catch (error) {
        openErrorDialog({
          message: errorText as ElementType,
          error,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      maybeAddPaymentMethod,
      loadAmount,
      reloadPaymentMethodId,
      getPrepaidPaymentMethod,
      addPrepaidCardMutation,
      setDefaultPaymentMethodId,
      getPaymentMethods,
      logger,
      openErrorDialog,
      formatMessage,
      errorText,
    ]
  );

  const handleSubmit = useCallback(() => {
    setLoading(true);
    if (applePaySelected && !showAddNewPaymentMethodForm) {
      return requestApplePayPayment({
        countryCode,
        currencyCode,
        total: loadAmount,
      });
    }
    if (googlePaySelected && !showAddNewPaymentMethodForm) {
      // Get the prepaid gateway configs for google pay
      const { gateway, gatewayMerchantId } = getGooglePaymentsConfigs({
        transactionType: TransactionType.PREPAID,
        countryCode,
      });
      return requestGooglePayPayment({
        countryCode,
        currencyCode,
        total: loadAmount,
        gatewayMerchantId,
        gateway,
      });
    }
    addNewPrepaidAccount();
  }, [
    applePaySelected,
    googlePaySelected,
    showAddNewPaymentMethodForm,
    addNewPrepaidAccount,
    requestApplePayPayment,
    requestGooglePayPayment,
    countryCode,
    currencyCode,
    loadAmount,
  ]);

  // disable the button if:
  // - The form is hidden and no reload method is selected
  const disabled = !showAddNewPaymentMethodForm && !reloadPaymentMethodId;
  const modalHeading = formatMessage({ id: 'loadYourTimCard' });

  // Show the verification modal if the user:
  // - Is unverified
  // - Doesn't have existing payment methods
  // - Apple Pay is not enabled
  // - Google Pay is not enabled
  if (userHasToVerify && !showExistingCardSection && !canUseApplePay && !canUseGooglePay) {
    return <UserVerificationModal onDismiss={onDismiss} />;
  }

  return (
    <Modal
      onDismiss={onDismiss}
      mParticleEventData={{
        modalAppearanceEventMessage,
      }}
    >
      <ModalContent>
        <ModalHeading>{modalHeading}</ModalHeading>
        {section === ModalStates.FORM && (
          <>
            <div>
              <SectionHeading>{formatMessage({ id: 'welcomeToYour' })}</SectionHeading>
              <TimCardText>{formatMessage({ id: 'emptyStateTimCardHeadline' })}</TimCardText>
            </div>
            <InstructionText>{formatMessage({ id: 'timCardInstructions' })}</InstructionText>
            <SubInstructionText>
              {formatMessage(
                { id: 'letsLoadX' },
                {
                  amount: formatCurrencyForUser(loadAmount),
                }
              )}
            </SubInstructionText>

            {!userHasToVerify && (
              <SpacedContent>
                <LeftAlignedContent>
                  <SubheadingContainer>
                    <SubheadingButton
                      onClick={toggleAddPaymentMethodForm}
                      data-testid="new-payment-toggle"
                    >
                      {formatMessage({ id: 'paymentMethod' })}
                    </SubheadingButton>
                    <DisplayToggle>
                      <RotateIcon active={showAddNewPaymentMethodForm}>
                        <Icon icon="select" color="icon-default" width="24px" aria-hidden />
                      </RotateIcon>
                    </DisplayToggle>
                  </SubheadingContainer>
                </LeftAlignedContent>
                <CollapsibleContent
                  collapsed={!showAddNewPaymentMethodForm}
                  data-testid="add-new-payment"
                >
                  <FormSection>
                    <CreditCardFormInputs
                      onChange={handleChange}
                      paymentValues={paymentValues}
                      errors={errors}
                      showSaveCard={false}
                    />
                  </FormSection>
                </CollapsibleContent>
              </SpacedContent>
            )}

            {(showExistingCardSection || canUseApplePay) && (
              <SpacedContent>
                <LeftAlignedContent>
                  <SubheadingContainer>
                    <SubheadingButton onClick={toggleExistingPaymentMethodForm}>
                      {formatMessage({ id: 'selectPaymentMethod' })}
                    </SubheadingButton>
                    <DisplayToggle>
                      <RotateIcon active={showExistingPaymentMethodForm}>
                        <Icon icon="select" color="icon-default" width="24px" aria-hidden />
                      </RotateIcon>
                    </DisplayToggle>
                  </SubheadingContainer>
                </LeftAlignedContent>
                <CollapsibleContent
                  collapsed={!showExistingPaymentMethodForm}
                  data-testid="use-existing-payment"
                >
                  <>
                    <SectionDescription>
                      {formatMessage({ id: 'selectPaymentMethodToAddFunds' })}
                    </SectionDescription>
                    <PaymentMethodSelect
                      selectedMethod={reloadPaymentMethod}
                      nonSelectedMethods={allowedReloadPaymentMethods}
                      onOptionClick={handlePaymentMethodOptionClick}
                      hoverTheme={HoverTheme.LIGHT}
                    />
                  </>
                </CollapsibleContent>
              </SpacedContent>
            )}

            <Box width="100%" center margin="0 0 2rem 0">
              <ActionButton
                isLoading={loading}
                disabled={disabled}
                onClick={handleSubmit}
                data-testid="load-timcard-submit"
              >
                {formatMessage(
                  { id: 'loadXOnTimCard' },
                  {
                    amount: formatCurrencyForUser(loadAmount),
                  }
                )}
              </ActionButton>
            </Box>
          </>
        )}
        <ErrorDialog />
        {isModalStateSuccess && (
          <CenteredContent>
            <Suspense fallback={<PlacementHolder height="57px" width="57px" />}>
              <IconCheckCircle width="57" height="57" />
            </Suspense>
            <SuccessHeading data-testid="success">
              {formatMessage({ id: 'success' })}
            </SuccessHeading>
            <NormalText>
              {formatMessage(
                { id: 'yourNewBalanceX' },
                {
                  newBalance: formatCurrencyForUser(prepaidCardBalance),
                }
              )}
            </NormalText>
          </CenteredContent>
        )}
      </ModalContent>
    </Modal>
  );
};

export default LoadPrepaidCardModal;
